/**
 * Billing Success Page
 *
 * Shown after successful Stripe checkout completion.
 *
 * @module pages/settings/BillingSuccessPage
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks';
import { Card, Button, Spinner } from '@/components/ui';
import { CheckCircle, ArrowRight, Key, Users, BarChart3 } from 'lucide-react';

// =============================================================================
// Component
// =============================================================================

export function BillingSuccessPage() {
  usePageTitle('Subscription Confirmed');

  const navigate = useNavigate();
  const { refreshOrganization } = useAuth();

  const [isRefreshing, setIsRefreshing] = useState(true);

  // Refresh organization data to get updated plan info
  useEffect(() => {
    const refresh = async () => {
      try {
        // Give Stripe webhook time to process
        await new Promise((resolve) => setTimeout(resolve, 2000));
        await refreshOrganization?.();
      } finally {
        setIsRefreshing(false);
      }
    };

    refresh();
  }, [refreshOrganization]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center animate-fade-in">
      <Card className="max-w-lg w-full text-center py-12 px-8">
        {isRefreshing ? (
          <div className="space-y-4">
            <Spinner size="lg" className="mx-auto" />
            <p className="text-gray-500">Setting up your subscription...</p>
          </div>
        ) : (
          <>
            {/* Success Icon */}
            <div className="mx-auto w-16 h-16 rounded-full bg-success-100 flex items-center justify-center mb-6">
              <CheckCircle className="h-10 w-10 text-success-500" />
            </div>

            {/* Success Message */}
            <h1 className="text-2xl font-display font-bold text-gray-900 mb-2">
              You've upgraded successfully!
            </h1>
            <p className="text-gray-500 mb-8">
              You've unlocked access to all your new plan features.
            </p>

            {/* Next Steps */}
            <div className="space-y-4 mb-8">
              <button
                onClick={() => navigate('/keys')}
                className="w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50/50 transition-colors text-left group"
              >
                <div className="p-2 bg-brand-100 rounded-lg text-brand-600 group-hover:bg-brand-200">
                  <Key className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Create API Keys</p>
                  <p className="text-sm text-gray-500">
                    Generate more API keys for your applications.
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-brand-500" />
              </button>

              <button
                onClick={() => navigate('/settings/team')}
                className="w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50/50 transition-colors text-left group"
              >
                <div className="p-2 bg-gray-100 rounded-lg text-gray-600 group-hover:bg-brand-100 group-hover:text-brand-600">
                  <Users className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Invite Team Members</p>
                  <p className="text-sm text-gray-500">
                    Collaborate with your team on API projects.
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-brand-500" />
              </button>

              <button
                onClick={() => navigate('/overview')}
                className="w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50/50 transition-colors text-left group"
              >
                <div className="p-2 bg-gray-100 rounded-lg text-gray-600 group-hover:bg-brand-100 group-hover:text-brand-600">
                  <BarChart3 className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">View Dashboard</p>
                  <p className="text-sm text-gray-500">
                    Monitor your API usage in real-time.
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-brand-500" />
              </button>
            </div>

            {/* Primary CTA */}
            <Button
              fullWidth
              onClick={() => navigate('/')}
              rightIcon={<ArrowRight className="h-4 w-4" />}
            >
              Go to Dashboard
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
